import { CommonModule } from '@angular/common';
import type { CdkDragDrop } from '@angular/cdk/drag-drop';
import { CdkDrag, CdkDragHandle, CdkDropList, CdkDropListGroup } from '@angular/cdk/drag-drop';
import type { OnInit } from '@angular/core';
import { ChangeDetectionStrategy, Component, DestroyRef, computed, inject, input, signal } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { DomSanitizer, type SafeHtml } from '@angular/platform-browser';

import type { Observable } from 'rxjs';

import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { TooltipModule } from 'ngx-bootstrap/tooltip';

import { ERadioGroupType, SpRadioGroupComponent } from '@ui/sp-radio-group';
import { SpControlHandleComponent } from '@ui/sp-control-handle/src/sp-control-handle.component';
import { SpSwitchComponent } from '@ui/sp-switch/src/sp-switch.component';
import { SpVariablesPopoverComponent } from '@ui/sp-variables-popover/src/sp-variables-popover.component';

import {
    EButtonLinkType,
    EButtonStyleType,
    EFormInputType,
    EHtmlAutocompleteAttr,
    EHtmlInputType,
    ESize,
    type EHorizontalPosition,
} from '@common/enums';
import { ELoginEduDefaultForm } from '@common/enums/login-edu-default-form.enum';
import { ELoginEduView } from '@common/enums/login-edu-view-form.enum';
import { LoginEduEnabledForm } from '@common/enums/login-edu-enabled-form.enum';

import type { CollectionStateStatuses } from '@common/adapters/collections';
import type { CrmContact } from '@integrations/crm-contacts';
import type { EduCourse } from '@common/types/edu-course.type';

import { commonIconTemplates } from '@common/icons/common-icons';
import { HtmlToTextPipe } from '@common/pipes/html-to-text.pipe';

import type {
    AnalyticsSettingsLegacy,
    ExtendedPositionLegacy,
    FormWidgetElement,
    LoginEduAuthorizationButtonLegacy,
    LoginEduButtonLegacy,
    LoginEduRegistrationButtonLegacy,
} from '@sites/entity-core';
import type { LoginEduForm, LoginEduZeroForm } from '@sites/entity-signal-form';

import { EPixelTypes } from '@common/types/analytics-setings.type';
import { WebAnalyticsSettingsSignalLegacyComponent } from '@web/shared/components/web-analytics-settings-signal-legacy/web-analytics-settings-signal-legacy.component';

import { injectCrmContactsQuery } from '@integrations/crm-contacts';
import { EduCoursesCollectionFacade } from '@web/store/collections/edu-courses/edu-courses.facade';
import { AppFacade } from '@web/store/app/app.facade';
import { LayoutFacade } from '@web/store/layout/layout.facade';
import { UserFacade } from '@web/store/user/user.facade';
import { isLoginEduNewUser } from '@web/widgets/login-edu/constants/login-edu.const';
import { WebLegacyWidgetSignalFormService } from '@web/core/services/web-legacy-widget-signal-form.service';

import type { WebLegacyWidgetEntity } from '@web/types';
import type { LoginEduWidgetData } from '@web/widgets/login-edu/type';

import {
    DEFAULT_BUTTON_BASE,
    DEFAULT_BUTTON_HOVER_STATES,
} from '@web/widgets/common/base/base-button/base-button.const';

import { WebCustomIdComponent } from '@web/shared/components/web-custom-id/web-custom-id.component';
import { WebSettingsModule } from '@web/shared/components/web-settings/web-settings.module';
import { WebWidgetHideSettingsComponent } from '@web/shared/components/web-widget-hide-settings/web-widget-hide-settings.component';
import { WebWidgetExtendedHideSettingsComponent } from '@web/shared/components/web-widget-extended-hide-settings';
import { SpExtendedPositionSignalSettingsComponent } from '@components/sp-extended-position-signal-settings';
import { WebWidgetPositionSettingsComponent } from '@web/shared/components/web-widget-position-settings/web-widget-position-settings.component';

import {
    CourseTariffSelectorComponent,
    type CourseTariffSelection,
} from '@web/widgets/common/course-tariff-selector/course-tariff-selector.component';

import { AuthorizationButtonSettingsComponent } from './authorization-button-settings/authorization-button-settings.component';
import { CheckboxButtonSettingsComponent } from './checkbox-button-settings/checkbox-button-settings.component';
import { DefaultButtonSettingsComponent } from './default-button-settings/default-button-settings.component';
import {
    FieldElementSettingsComponent,
    type LoginEduFormType,
} from './field-element-settings/field-element-settings.component';
import { FieldInputSettingsComponent } from './field-input-settings/field-input-settings.component';
import { LoginAdditionalSettingsComponent } from './login-additional-settings/login-additional-settings.component';
import { RegistrationButtonSettingsComponent } from './registration-button-settings/registration-button-settings.component';
import { RegistrationTriggerSettingsComponent } from './registration-trigger-settings/registration-trigger-settings.component';
import { AuthorizationTriggerSettingsComponent } from './authorization-trigger-settings/authorization-trigger-settings.component';

enum CurrentSettings {
    MAIN = 'main',
    DEFAULT_BUTTON = 'defaultButton',
    AUTHORIZATION_BUTTON = 'authorizationButton',
    REGISTRATION_BUTTON = 'registrationButton',
    ELEMENT = 'element',
    INPUT = 'input',
    ADDITIONAL = 'additional',
    CHECKBOX = 'checkbox',
    ANALYTICS = 'analytics',
    REGISTRATION_TRIGGER = 'registrationTrigger',
    AUTHORIZATION_TRIGGER = 'authorizationTrigger',
}

/**
 * Panels that edit the widget signal form **live**, so leaving them with
 * "cancel" has to roll the edits back. Each opens a transaction and
 * {@link LoginEduControlComponent.closeSettings} commits or rolls it back.
 *
 * Every sub-panel is on this list except analytics, which is absent for the same
 * reason as in the form widget — it has always applied live with no save/reset.
 */
const TRANSACTIONAL_PANELS: ReadonlySet<CurrentSettings> = new Set([
    CurrentSettings.INPUT,
    CurrentSettings.ELEMENT,
    CurrentSettings.ADDITIONAL,
    CurrentSettings.DEFAULT_BUTTON,
    CurrentSettings.AUTHORIZATION_BUTTON,
    CurrentSettings.REGISTRATION_BUTTON,
    CurrentSettings.CHECKBOX,
    CurrentSettings.REGISTRATION_TRIGGER,
    CurrentSettings.AUTHORIZATION_TRIGGER,
]);

/** Settings panel per button kind — each writes into the signal form directly. */
const BUTTON_PANELS: Readonly<Record<LoginEduButtonLegacy['buttonType'], CurrentSettings>> = {
    default: CurrentSettings.DEFAULT_BUTTON,
    authorization: CurrentSettings.AUTHORIZATION_BUTTON,
    registration: CurrentSettings.REGISTRATION_BUTTON,
};

@Component({
    selector: 'web-login-edu-control',
    templateUrl: './login-edu-control.component.html',
    styleUrls: ['./login-edu-control.component.less'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,

        CdkDrag,
        CdkDragHandle,
        CdkDropList,
        CdkDropListGroup,

        TooltipModule,
        TranslateModule,

        SpControlHandleComponent,
        SpRadioGroupComponent,
        SpSwitchComponent,
        SpVariablesPopoverComponent,
        WebAnalyticsSettingsSignalLegacyComponent,
        WebCustomIdComponent,
        WebSettingsModule,

        HtmlToTextPipe,
        WebWidgetHideSettingsComponent,
        WebWidgetExtendedHideSettingsComponent,
        WebWidgetPositionSettingsComponent,
        SpExtendedPositionSignalSettingsComponent,

        AuthorizationButtonSettingsComponent,
        CheckboxButtonSettingsComponent,
        CourseTariffSelectorComponent,
        DefaultButtonSettingsComponent,
        FieldElementSettingsComponent,
        FieldInputSettingsComponent,
        LoginAdditionalSettingsComponent,
        RegistrationButtonSettingsComponent,
        RegistrationTriggerSettingsComponent,
        AuthorizationTriggerSettingsComponent,
    ],
})
export class LoginEduControlComponent implements OnInit {
    public readonly widget = input.required<WebLegacyWidgetEntity<LoginEduWidgetData>>();
    public readonly isSpecific = input<boolean>(false);
    public readonly isEduSite = input<boolean>(false);

    private readonly signalFormService: WebLegacyWidgetSignalFormService = inject(WebLegacyWidgetSignalFormService);
    private readonly eduCourseFacade: EduCoursesCollectionFacade = inject(EduCoursesCollectionFacade);
    private readonly layoutFacade: LayoutFacade = inject(LayoutFacade);
    private readonly appFacade: AppFacade = inject(AppFacade);
    private readonly translateService: TranslateService = inject(TranslateService);
    private readonly destroyRef: DestroyRef = inject(DestroyRef);
    protected readonly sanitizer: DomSanitizer = inject(DomSanitizer);

    public form!: LoginEduForm;

    private readonly isAdmin = toSignal(this.appFacade.isAdmin$, { initialValue: false });
    private readonly userCreatedAt = toSignal(inject(UserFacade).memberAreaUserCreatedAt$, { initialValue: null });
    public readonly isForNewUser = computed(() => isLoginEduNewUser(this.userCreatedAt()));

    protected readonly crmContactsQuery = injectCrmContactsQuery(() => ({ enabled: !this.isAdmin() }));
    public readonly crmContacts = computed<CrmContact[]>(() =>
        this.isAdmin() ? [] : (this.crmContactsQuery.data() ?? [])
    );

    public readonly eduCoursesStatuses$: Observable<CollectionStateStatuses> = this.eduCourseFacade.eduCoursesStatuses$;
    public readonly isDeviceMobile$: Observable<boolean> = this.layoutFacade.isDeviceMobile$;
    public readonly isDeviceMobileSig = toSignal(this.layoutFacade.isDeviceMobile$, { initialValue: false });
    public readonly isDeviceTabletSig = toSignal(this.layoutFacade.isDeviceTablet$, { initialValue: false });

    public readonly radioGroupType = ERadioGroupType;

    public readonly currentSettings = signal<CurrentSettings>(CurrentSettings.MAIN);
    public readonly editableButtonIndex = signal<number | null>(null);
    public readonly editableCheckboxIndex = signal<number | null>(null);
    public readonly editedInputIndex = signal<number | null>(null);
    public readonly editedInputType = signal<LoginEduFormType | null>(null);

    public readonly settingEnum = CurrentSettings;

    public readonly EMPTY_LINK_TEXT = this.translateService.instant('provide_link');

    public ngOnInit(): void {
        // A transactional sub-panel still open when the settings panel is torn
        // down (widget deselected, page switched) would leave its frame open,
        // and an open frame suppresses every update emission, freezing the
        // form. Unwind by rolling back: closing without Save discards the
        // sub-panel's edits, as in the form/payment-form `FormSettingsPanel`.
        // Registered before `getOrCreateScoped`'s `release`, so it still runs
        // on a retained form.
        this.destroyRef.onDestroy(() => {
            while (this.form?.inTransaction()) {
                this.form.rollbackTransaction();
            }
        });

        this.form = this.signalFormService.getOrCreateScoped<LoginEduForm>(this.widget().id, this.destroyRef);
    }

    /** True when the widget lives inside a zero block — drives template branching. */
    public get isZero(): boolean {
        return this.widget().type === 'zero';
    }

    /** The form narrowed to the zero variant. */
    public get zeroForm(): LoginEduZeroForm {
        return this.form as LoginEduZeroForm;
    }

    /** See the video control's `zeroPosition` — same contract. */
    public readonly zeroPosition = computed<ExtendedPositionLegacy | null>(() => {
        const f = this.form as LoginEduZeroForm | undefined;
        if (!f || !this.isZero) return null;
        return f.position().value() as ExtendedPositionLegacy;
    });

    // --- Derived button lookups ---

    public readonly authorizationButtonIndex = computed<number>(() =>
        this.form
            .buttons()
            .value()
            .findIndex((button) => button.buttonType === 'authorization')
    );

    public readonly signUpButtonIndex = computed<number>(() =>
        this.form
            .buttons()
            .value()
            .findIndex((button) => button.buttonType === 'registration')
    );

    public readonly authorizationButton = computed<LoginEduAuthorizationButtonLegacy | undefined>(
        () =>
            this.form.buttons().value()[this.authorizationButtonIndex()] as
                | LoginEduAuthorizationButtonLegacy
                | undefined
    );

    public readonly signUpButton = computed<LoginEduRegistrationButtonLegacy | undefined>(
        () => this.form.buttons().value()[this.signUpButtonIndex()] as LoginEduRegistrationButtonLegacy | undefined
    );

    public readonly registrationTrigger = computed(() => this.signUpButton()?.modalTrigger ?? null);
    public readonly authorizationTrigger = computed(() => this.authorizationButton()?.modalTrigger ?? null);

    // --- View-mode predicates ---

    public readonly isStaticFormView = computed<boolean>(() => this.form.formView().value() === ELoginEduView.static);
    public readonly isModalFormView = computed<boolean>(() => this.form.formView().value() === ELoginEduView.modal);
    public readonly isDefaultRegisterForm = computed<boolean>(
        () => this.isForNewUser() || this.form.defaultForm().value() === ELoginEduDefaultForm.register
    );
    public readonly isDefaultLoginForm = computed<boolean>(
        () => !this.isForNewUser() && this.form.defaultForm().value() === ELoginEduDefaultForm.login
    );

    public readonly hasRegistrationForm = computed<boolean>(() => Boolean(this.signUpButton()?.isActive));
    public readonly hasAuthorizationForm = computed<boolean>(() => Boolean(this.authorizationButton()?.isActive));

    public readonly isBothViewForm = computed<boolean>(() => this.hasRegistrationForm() && this.hasAuthorizationForm());
    public readonly isRegisterViewForm = computed<boolean>(
        () => this.hasRegistrationForm() && !this.hasAuthorizationForm()
    );
    public readonly isLoginViewForm = computed<boolean>(
        () => !this.hasRegistrationForm() && this.hasAuthorizationForm()
    );

    public readonly isEditingButton = computed<boolean>(
        () => this.editableButtonIndex() !== null || this.currentSettings() !== CurrentSettings.MAIN
    );

    public readonly loginEduEnabledForm = computed<LoginEduEnabledForm>(() => {
        if (this.isForNewUser() || !this.hasAuthorizationForm()) return LoginEduEnabledForm.register;
        if (!this.hasRegistrationForm()) return LoginEduEnabledForm.login;
        return LoginEduEnabledForm.both;
    });

    public readonly showCourseTariffSelector = computed<boolean>(() =>
        this.isModalFormView() ? Boolean(this.registrationTrigger()?.isActive) : Boolean(this.signUpButton()?.isActive)
    );

    public readonly isAuthorizationFormFieldsDisabled = computed<boolean>(() =>
        this.isModalFormView() ? !this.authorizationTrigger()?.isActive : this.isRegisterViewForm()
    );

    public readonly isRegistrationFormFieldsDisabled = computed<boolean>(() =>
        this.isModalFormView() ? !this.registrationTrigger()?.isActive : this.isLoginViewForm()
    );

    public readonly showAuthorizationButtonSettings = computed<boolean>(() =>
        this.isModalFormView()
            ? Boolean(this.authorizationTrigger()?.isActive) && this.isDefaultLoginForm()
            : this.isDefaultLoginForm()
    );

    public readonly showRegistrationButtonSettings = computed<boolean>(() =>
        this.isModalFormView()
            ? Boolean(this.registrationTrigger()?.isActive) && this.isDefaultRegisterForm()
            : this.isDefaultRegisterForm()
    );

    public readonly showCheckboxRegistrationSettings = computed<boolean>(() =>
        this.isModalFormView()
            ? Boolean(this.registrationTrigger()?.isActive) && this.isDefaultRegisterForm()
            : this.hasRegistrationForm() && this.isDefaultRegisterForm()
    );

    public readonly showAdditionalButtons = computed<boolean>(() =>
        this.isModalFormView()
            ? Boolean(this.registrationTrigger()?.isActive) && this.isDefaultRegisterForm()
            : this.hasRegistrationForm()
    );

    // --- Panel switching (transactions) ---

    public setCurrentSettingsType(panel: CurrentSettings): void {
        if (TRANSACTIONAL_PANELS.has(panel)) {
            this.form.beginTransaction();
        }

        this.currentSettings.set(panel);
    }

    public closeSettings(commit: boolean): void {
        if (TRANSACTIONAL_PANELS.has(this.currentSettings())) {
            if (commit) {
                this.form.commitTransaction();
            } else {
                this.form.rollbackTransaction();
            }
        }

        this.currentSettings.set(CurrentSettings.MAIN);
        this.editableButtonIndex.set(null);
        this.editableCheckboxIndex.set(null);
        this.editedInputIndex.set(null);
        this.editedInputType.set(null);
    }

    /**
     * Opens (or closes, on `null`) one button's settings panel — see
     * {@link BUTTON_PANELS}. Goes through {@link setCurrentSettingsType} so the
     * panels that edit the signal form live get their transaction.
     */
    public editButton(index: number | null): void {
        if (index === null) {
            this.currentSettings.set(CurrentSettings.MAIN);
            this.editableButtonIndex.set(null);
            return;
        }

        const buttonType = this.form.buttons().value()[index]?.buttonType;
        if (!buttonType) return;

        this.editableButtonIndex.set(index);
        this.setCurrentSettingsType(BUTTON_PANELS[buttonType]);
    }

    public editCheckbox(index: number | null): void {
        if (index === null) {
            this.currentSettings.set(CurrentSettings.MAIN);
            this.editableCheckboxIndex.set(null);
            return;
        }

        this.editableCheckboxIndex.set(index);
        this.setCurrentSettingsType(CurrentSettings.CHECKBOX);
    }

    public editInput(index: number, type: LoginEduFormType): void {
        this.editedInputIndex.set(index);
        this.editedInputType.set(type);
        this.setCurrentSettingsType(CurrentSettings.ELEMENT);
    }

    public editRegistrationModalTrigger(): void {
        this.setCurrentSettingsType(CurrentSettings.REGISTRATION_TRIGGER);
    }

    public editAuthorizationModalTrigger(): void {
        this.setCurrentSettingsType(CurrentSettings.AUTHORIZATION_TRIGGER);
    }

    // --- Writes ---

    public setHorizontalPosition(value: string): void {
        // The widget's own alignment and the rendered form's alignment are kept
        // in step — the original control set both controls together.
        this.form.transact(() => {
            this.form.setHorizontalPosition(value as EHorizontalPosition);
            this.form.setInputSettings({
                ...this.form.inputSettings().value(),
                horizontalPosition: value as EHorizontalPosition,
            });
        });
    }

    public setDefaultForm(value: ELoginEduDefaultForm): void {
        this.form.transact(() => {
            this.form.setDefaultForm(value);

            if (this.isBothViewForm()) {
                return;
            }

            this.setFormsActive(value === ELoginEduDefaultForm.login);
        });
    }

    /**
     * The "also show the other form" checkbox. Unchecking it leaves whichever
     * form is currently the default enabled; checking it enables both.
     */
    public toggleEnabledForm(): void {
        this.form.transact(() => {
            if (!this.isBothViewForm()) {
                this.setFormsActive(true, true);
                return;
            }

            const isLoginDefault = this.form.defaultForm().value() === ELoginEduDefaultForm.login;
            this.setFormsActive(isLoginDefault);
            this.form.setDefaultForm(isLoginDefault ? ELoginEduDefaultForm.login : ELoginEduDefaultForm.register);
        });
    }

    /**
     * Flips the two auth buttons' `isActive` flags. `bothActive` turns both on
     * (the "show both forms" case); otherwise `authorizationActive` picks which
     * single form stays enabled.
     */
    private setFormsActive(authorizationActive: boolean, bothActive = false): void {
        const authIndex = this.authorizationButtonIndex();
        const signUpIndex = this.signUpButtonIndex();

        if (authIndex !== -1) {
            this.form.updateButton(authIndex, { isActive: bothActive || authorizationActive });
        }
        if (signUpIndex !== -1) {
            this.form.updateButton(signUpIndex, { isActive: bothActive || !authorizationActive });
        }
    }

    public toggleRegistrationCheckbox(value: boolean): void {
        this.form.setRegistrationCheckbox(value);
    }

    public toggleLinkBetweenForms(active: boolean): void {
        this.form.setLinkBetweenForms(active);
    }

    public toggleRegistrationModalTrigger(active: boolean): void {
        const index = this.signUpButtonIndex();
        const button = this.signUpButton();
        if (index === -1 || !button) return;

        this.form.transact(() => {
            // Turning the default form's trigger off would leave the widget
            // pointing at a form the visitor cannot open — switch the default.
            if (this.isDefaultRegisterForm()) {
                this.form.setDefaultForm(ELoginEduDefaultForm.login);
            }
            this.form.updateButton(index, { modalTrigger: { ...button.modalTrigger, isActive: active } });
        });
    }

    public toggleAuthorizationModalTrigger(active: boolean): void {
        const index = this.authorizationButtonIndex();
        const button = this.authorizationButton();
        if (index === -1 || !button) return;

        this.form.transact(() => {
            if (this.isDefaultLoginForm()) {
                this.form.setDefaultForm(ELoginEduDefaultForm.register);
            }
            this.form.updateButton(index, { modalTrigger: { ...button.modalTrigger, isActive: active } });
        });
    }

    public onAnalyticsSettingsChange(value: AnalyticsSettingsLegacy): void {
        this.form.setAnalyticsSettings(value);
    }

    public setPosition(value: LoginEduWidgetData['position']): void {
        this.form.setPosition(value as never);
    }

    public setHideOnDesktop(value: boolean): void {
        this.form.setHideOnDesktop(value);
    }

    public setHideOnTablet(value: boolean): void {
        this.form.setHideOnTablet(value);
    }

    public setHideOnMobile(value: boolean): void {
        this.form.setHideOnMobile(value);
    }

    public updateWidgetDisplayId(value: string): void {
        this.form.setDisplayId(value);
    }

    public handleCourseTariffSelection(selection: CourseTariffSelection): void {
        this.form.transact(() => {
            if (selection.tariffId) {
                this.form.setCourseTariffId(selection.tariffId);
            }

            if (selection.paidCourseInfo) {
                this.form.setCourseTariffDetails({
                    tariffName: selection.paidCourseInfo.tariffName,
                    isPaid: selection.paidCourseInfo.isPaid,
                    eduPaymentId: selection.paidCourseInfo.eduPaymentId,
                    price: selection.paidCourseInfo.price,
                    paymentMethodId: selection.paidCourseInfo.paymentMethodId,
                    paymentType: selection.paidCourseInfo.paymentType,
                    paymentName: selection.paidCourseInfo.paymentName,
                    currency: selection.paidCourseInfo.currency,
                });
            } else {
                this.form.setCourseTariffDetails(null);
            }

            this.setEduCourse(selection.course);
        });
    }

    private setEduCourse(course: EduCourse | null): void {
        if (!course) {
            this.form.setCourse(null);
            return;
        }

        // Only the fields the published page needs are persisted — the widget
        // is not a mirror of the courses API.
        this.form.setCourse({
            id: course.id,
            name: course.name,
            description: course.description,
            domainId: course.domainId,
            landingId: course?.landingId,
            userId: course.userId,
            status: course.status,
        });
    }

    // --- Buttons ---

    public addButton(): void {
        this.form.addButton({
            buttonType: 'default',
            label: this.translateService.instant('control_learn_more'),
            styleType: EButtonStyleType.secondary,
            size: ESize.M,
            link: {
                type: EButtonLinkType.link,
                url: null,
            },
            isTargetBlank: false,
            buttonBase: { ...DEFAULT_BUTTON_BASE },
            buttonHover: { ...DEFAULT_BUTTON_HOVER_STATES },
        } as unknown as LoginEduButtonLegacy);
    }

    public removeButton(index: number, button: LoginEduButtonLegacy): void {
        if (button.buttonType !== 'default') {
            return;
        }

        this.form.removeButton(index);
    }

    public dropGroup(event: CdkDragDrop<unknown>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        this.form.moveButton(event.previousIndex, event.currentIndex);
    }

    // --- Checkboxes ---

    public addCheckbox(): void {
        this.form.addCheckbox({
            buttonType: 'checkbox',
            label: this.translateService.instant('control_registration_checkbox'),
            crmContactVariable: null,
            crmVariableType: null,
            required: false,
        });
    }

    public removeCheckbox(index: number): void {
        this.form.removeCheckbox(index);
    }

    public dropCheckboxGroup(event: CdkDragDrop<unknown>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        this.form.moveCheckbox(event.previousIndex, event.currentIndex);
    }

    // --- Sign-up fields ---

    public addInput(): void {
        this.form.addSignUpElement({
            type: EFormInputType.input,
            label: this.translateService.instant('control_entry_field'),
            variable: null,
            crmContactVariable: null,
            crmDealVariable: null,
            placeholder: this.translateService.instant('control_input_placeholder'),
            required: false,
            protected: false,
            isDynamicPlaceholderEnabled: false,
            htmlInputType: EHtmlInputType.text,
            autocompleteType: EHtmlAutocompleteAttr.off,
        } as unknown as FormWidgetElement);
    }

    /**
     * The list is passed in explicitly: this is called from the main panel,
     * where `editedInputType` (which only tracks the open field-settings panel)
     * is still null.
     */
    public removeInput(index: number, type: LoginEduFormType): void {
        if (type === 'signIn') {
            this.form.removeSignInElement(index);
            return;
        }

        this.form.removeSignUpElement(index);
    }

    public dropElementItem(event: CdkDragDrop<unknown>): void {
        if (event.previousIndex === event.currentIndex) {
            return;
        }

        this.form.moveSignUpElement(event.previousIndex, event.currentIndex);
    }

    // --- Presentation helpers ---

    public getButtonLabel(button: LoginEduButtonLegacy): string {
        if (button.buttonType !== 'registration') {
            return button.label;
        }

        return button.registrationButton.label;
    }

    public getButtonSubText(button: LoginEduButtonLegacy): string {
        if (button.buttonType === 'default') {
            return button.link?.url?.length ? button.link.url : this.EMPTY_LINK_TEXT;
        }

        if (button.buttonType === 'registration') {
            return this.translateService.instant('control_registration_form');
        }

        return this.translateService.instant('control_authorization_form');
    }

    public getButtonWarningColorText(button: LoginEduButtonLegacy): boolean {
        if (button.buttonType === 'default') {
            return Boolean(!button.link?.url?.length);
        }

        return false;
    }

    public getDisabledButtonTooltip(button: LoginEduButtonLegacy): string | null {
        if (this.isDefaultButtonType(button)) {
            return null;
        }

        return this.translateService.instant('control_system_button_delete');
    }

    public isDefaultButtonType(button: LoginEduButtonLegacy): boolean {
        return button.buttonType === 'default';
    }

    public isDisabledDelete(element: FormWidgetElement): boolean {
        return Boolean(element.protected) && element.type !== EFormInputType.phone;
    }

    public getControlHandleIcon(type: string): string {
        return `./assets/img/websites/forms-icons/lpc-f-${type}-icon.svg`;
    }

    public get getControlIcon(): string {
        return `./assets/img/websites/forms-icons/lpc-f-checkbox-icon.svg`;
    }

    public getIcon(iconName: string): SafeHtml {
        return this.sanitizer.bypassSecurityTrustHtml(commonIconTemplates[iconName]);
    }

    protected readonly ELoginEduView = ELoginEduView;
    protected readonly ELoginEduDefaultForm = ELoginEduDefaultForm;
    protected readonly EPixelTypes = EPixelTypes;
    protected readonly LoginEduEnabledForm = LoginEduEnabledForm;
}
